/**
 * Who is running a clock right now, held once for the whole shell.
 *
 * ONE READ ANSWERS THREE SCREENS: the counter in the top bar, the start/stop
 * button on a task, and the "somebody is on this" mark in the task register.
 * There is at most one clock per operator (the unique index in the CRM schema
 * says so), so the list is a handful of rows, and holding them in one place is
 * what stops three components asking the same question on every render.
 *
 * THE DIALOGS LIVE HERE TOO, and that is the part worth reading before adding a
 * fourth place to start a timer. Two of the three things a clock can do end in a
 * question - "you already have one running, stop it?" and "this has been going
 * eleven hours, how much of it was work?" - and the button that asked is often
 * inside a row menu that closes on the very click that asked. A dialog owned by
 * that button would unmount with it. Owned here, every surface gets the same
 * question, the same wording and the same refusal handling for free, and a new
 * button is one call to `startOn` or `requestStop`.
 *
 * WHAT THIS PROVIDER DOES NOT DO IS TICK. A context that re-rendered once a
 * second would re-render the entire CRM once a second for the sake of two digits
 * in the corner. The seconds live in `ElapsedClock`, which owns its own interval
 * and is the only thing on the page that repaints; everything here changes only
 * when a clock actually starts, stops or is thrown away.
 */
import {
  createContext, useCallback, useContext, useMemo, useRef, useState, useEffect, type ReactNode,
} from "react";
import { useT } from "../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../lib/auth";
import {
  discardTaskTimer, getRunningTimers, startTaskTimer, stopTaskTimer, timerRefusal,
  type RunningTimer, type StopTimerBody, type StopTimerResult,
} from "../../lib/taskTimer";
import { fmtHours } from "../sections/projects/lib";
import { useToast } from "../ui";
import { clockSkewMs } from "./elapsed";
import { TimerDialogs } from "./TimerDialogs";

/**
 * How often the list is re-read WHILE A CLOCK IS RUNNING.
 *
 * Nothing polls when none is: the only thing that can change then is a colleague
 * starting one, and hearing about that when the tab is next focused is soon
 * enough. A clock of one's own is different - it may have been stopped in
 * another tab, and a top bar still counting up for a clock that no longer exists
 * is a lie the operator acts on.
 */
const POLL_MS = 60_000;

/**
 * A start that has not happened yet, carried through whatever question stands in
 * its way.
 *
 * The note travels with the task id because the header's start dialog collects
 * both in one form, and a switch or a stale stop in between must not quietly
 * drop the sentence the operator typed.
 */
export interface StartIntent {
  taskId: string;
  note?: string;
}

/**
 * A question waiting for the operator, drawn by `TimerDialogs`.
 *
 * `startAfter` is the one piece of continuation state in the feature: switching
 * from a stale clock is "stop that one - no, first tell me what to record for it
 * - now start this one", and the start has to survive the dialog in between.
 * Without it the operator would answer the hard question and then find that the
 * thing they actually pressed never happened.
 */
/**
 * What came back from a stop attempt. See `stopNow` for why the refused case
 * carries a flag rather than being folded into a null.
 */
export type StopOutcome = { ok: true; result: StopTimerResult } | { ok: false; stale: boolean };

type Pending =
  /** "Which task?" - the header's own way in, where no task is implied by the
   *  page the operator is standing on. */
  | { kind: "start" }
  | { kind: "stop"; timer: RunningTimer; startAfter?: StartIntent }
  | { kind: "switch"; running: RunningTimer; intent: StartIntent }
  | { kind: "discard"; timer: RunningTimer }
  | null;

interface TimerValue {
  /** Every clock running in the CRM, mine included. */
  rows: RunningTimer[];
  /** Mine, or null. The unique index guarantees there is at most one. */
  mine: RunningTimer | null;
  /** The server's clock minus this browser's, in milliseconds. Handed to
   *  `elapsedSeconds` so the counter agrees with the hours that get recorded. */
  skewMs: number;
  /** Minutes after which the server refuses a one-click stop. */
  staleAfterMinutes: number;
  /** False until the first read has answered, so a screen can tell "no clocks"
   *  from "we have not asked yet" - only one of those is worth drawing. */
  ready: boolean;
  /** Whether this operator may run clocks at all. False switches the whole
   *  feature off: no read, no poll, no buttons. */
  enabled: boolean;
  /** True while a call is in flight, so a button can refuse to be pressed twice
   *  and produce two clocks' worth of confusion. */
  busy: boolean;
  /** Every clock on one task. More than one is legitimate: two people can work
   *  on the same thing at the same time, and the register has to show both. */
  onTask: (taskId: string) => RunningTimer[];
  refresh: () => void;

  /**
   * Start on this task, asking first if another clock is already running.
   *
   * Resolves TRUE only when a clock is now running. False means the start did
   * not happen - either it was refused, or a question took its place - and a
   * dialog that called this must not close itself on a false: the question the
   * provider just raised IS the dialog on screen now, and closing would throw it
   * away along with the operator's answer to it.
   */
  startOn: (taskId: string, note?: string) => Promise<boolean>;
  /** Stop, in one click when the clock is believable and with a question when
   *  it is not. */
  requestStop: (timer: RunningTimer) => Promise<void>;
  /** Throw a clock away without recording anything, after a confirmation. */
  requestDiscard: (timer: RunningTimer) => void;
  /** Put the "how much of this was work" question up for a clock, optionally
   *  continuing into a start once it has been answered. The switch dialog needs
   *  this: stopping the clock in the way can itself run into the stale rule. */
  askStop: (timer: RunningTimer, startAfter?: StartIntent) => void;

  /** The dialogs' own doors, deliberately not the ones a button should use:
   *  these ask nothing and refuse nothing. */
  stopNow: (timer: RunningTimer, body?: StopTimerBody) => Promise<StopOutcome>;
  discardNow: (timer: RunningTimer) => Promise<void>;
  startNow: (taskId: string, note?: string) => Promise<void>;

  /** Put the "which task?" form up. The header uses it; a task page never needs
   *  it, because the task is the page. */
  askStart: () => void;

  pending: Pending;
  closePending: () => void;
}

const EMPTY: RunningTimer[] = [];

const TimerContext = createContext<TimerValue | null>(null);

/**
 * Exported nullable, unlike `useCrmUser`.
 *
 * A screen can be mounted outside this provider on purpose - the ad-hoc preview
 * entry points, and any future surface that has no business starting clocks -
 * and the honest answer there is "no clock feature here", drawn as nothing. A
 * hook that threw would turn a missing button into a white page.
 */
export function useTimers(): TimerValue | null {
  return useContext(TimerContext);
}

export function TimerProvider({ user, children }: { user: StaffUser; children: ReactNode }) {
  const t = useT();
  const toast = useToast();
  // Gated on the right to WRITE one, not the right to read the list. The read
  // answers to `view:crm`, but somebody who cannot start or stop a clock has
  // nothing to do with the answer, and polling on their behalf would be a
  // request a minute for a chip they never see.
  const enabled = userCanPlatform(user, "manage:crm.projects");

  const [rows, setRows] = useState<RunningTimer[]>(EMPTY);
  const [skewMs, setSkewMs] = useState(0);
  const [staleAfterMinutes, setStaleAfterMinutes] = useState(0);
  const [ready, setReady] = useState(!enabled);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending>(null);
  // Bumped to ask for another read. A counter rather than a boolean so two
  // requests in the same tick still produce two reads.
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  const closePending = useCallback(() => setPending(null), []);

  // Held so the poll below can ask "is anything running" without the effect
  // depending on `rows` - which would tear the interval down and build a new one
  // every time a clock started or stopped.
  const anyRunning = useRef(false);
  anyRunning.current = rows.length > 0;

  useEffect(() => {
    if (!enabled) return;
    const ctrl = new AbortController();
    getRunningTimers(ctrl.signal)
      .then((res) => {
        setRows(res.rows);
        setSkewMs(clockSkewMs(res.now));
        setStaleAfterMinutes(res.staleAfterMinutes);
        setReady(true);
      })
      .catch(() => {
        // Left as it was. A failed read is not a statement that nothing is
        // running, and clearing the list on one would stop the operator's own
        // counter mid-afternoon because a proxy hiccuped.
      });
    return () => ctrl.abort();
  }, [enabled, nonce]);

  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      if (anyRunning.current) refresh();
    };
    const id = window.setInterval(tick, POLL_MS);
    // A tab that has been in the background may have missed a stop entirely, so
    // coming back always costs one read - whether or not anything was running
    // when it left.
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, refresh]);

  const mine = useMemo(() => rows.find((r) => r.userId === user.id) ?? null, [rows, user.id]);
  const onTask = useCallback((taskId: string) => rows.filter((r) => r.taskId === taskId), [rows]);

  // ── The plain calls. They ask nothing; the three below them do the asking. ──

  const startNow = useCallback<TimerValue["startNow"]>(
    async (taskId, note) => {
      setBusy(true);
      try {
        await startTaskTimer(taskId, note ? { note } : {});
      } finally {
        setBusy(false);
        refresh();
      }
    },
    [refresh],
  );

  /**
   * A three-way answer, and the third way is why it is not a boolean. "Stale"
   * is not a failure: it is the server asking a question, and the caller has to
   * be able to put that question up instead of reporting an error for something
   * the operator can still complete. Every other refusal is reported here and
   * swallowed, because they all mean the same thing to the person standing in
   * front of the screen - somebody got there first - and the reload that follows
   * is the answer.
   */
  const stopNow = useCallback<TimerValue["stopNow"]>(
    async (timer, body) => {
      setBusy(true);
      try {
        const result = await stopTaskTimer(timer.id, body ?? {});
        toast(t("timer.recorded", { hours: fmtHours(result.entry.minutes) }));
        return { ok: true, result };
      } catch (err) {
        const refusal = timerRefusal(err);
        if (refusal.code === "timer_stale") return { ok: false, stale: true };
        toast(refusal.code === "already_stopped" ? t("timer.alreadyStopped") : t("common.error"));
        return { ok: false, stale: false };
      } finally {
        setBusy(false);
        refresh();
      }
    },
    [refresh, t, toast],
  );

  const discardNow = useCallback<TimerValue["discardNow"]>(
    async (timer) => {
      setBusy(true);
      try {
        await discardTaskTimer(timer.id);
        toast(t("timer.discarded"));
      } catch {
        toast(t("common.error"));
      } finally {
        setBusy(false);
        refresh();
      }
    },
    [refresh, t, toast],
  );

  // ── The three a button should call ────────────────────────────────────────

  const startOn = useCallback<TimerValue["startOn"]>(
    async (taskId, note) => {
      setBusy(true);
      try {
        await startTaskTimer(taskId, note ? { note } : {});
        return true;
      } catch (err) {
        const refusal = timerRefusal(err);
        // The server sends the clock that is in the way, so the question can
        // name the task rather than telling the operator to go and find it.
        if (refusal.code === "timer_running" && refusal.running) {
          setPending({ kind: "switch", running: refusal.running, intent: { taskId, note } });
        } else {
          toast(refusal.code === "not_found" ? t("tasks.notFound") : t("common.error"));
        }
        return false;
      } finally {
        setBusy(false);
        refresh();
      }
    },
    [refresh, t, toast],
  );

  const askStart = useCallback<TimerValue["askStart"]>(() => {
    setPending({ kind: "start" });
  }, []);

  const askStop = useCallback<TimerValue["askStop"]>((timer, startAfter) => {
    setPending({ kind: "stop", timer, startAfter });
  }, []);

  const requestStop = useCallback<TimerValue["requestStop"]>(
    async (timer) => {
      // Asked before the call when the list already says the clock is stale, and
      // again after it when the server says so - the row on screen may have been
      // read hours ago, and the line is crossed while nobody is looking.
      if (timer.stale) {
        askStop(timer);
        return;
      }
      const out = await stopNow(timer);
      if (!out.ok && out.stale) askStop({ ...timer, stale: true });
    },
    [askStop, stopNow],
  );

  const requestDiscard = useCallback<TimerValue["requestDiscard"]>((timer) => {
    setPending({ kind: "discard", timer });
  }, []);


  const value = useMemo<TimerValue>(
    () => ({
      rows, mine, skewMs, staleAfterMinutes, ready, enabled, busy, onTask, refresh,
      startOn, requestStop, requestDiscard, askStart, askStop, stopNow, discardNow, startNow,
      pending, closePending,
    }),
    [
      rows, mine, skewMs, staleAfterMinutes, ready, enabled, busy, onTask, refresh,
      startOn, requestStop, requestDiscard, askStart, askStop, stopNow, discardNow, startNow,
      pending, closePending,
    ],
  );

  return (
    <TimerContext.Provider value={value}>
      {children}
      {/* Inside the provider and outside the routed area: a question about a
          clock must survive the page the operator was on when they asked it. */}
      <TimerDialogs />
    </TimerContext.Provider>
  );
}
